import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader.js';
import { clone as cloneSkeleton } from 'three/examples/jsm/utils/SkeletonUtils.js';
import { createModelLoadingManager } from './assetLoading';

export type ImportedModelCategory = 'terrain' | 'fortification' | 'resource';

export interface ImportedModelAsset {
  id: string;
  name: string;
  category: ImportedModelCategory;
  path: string;
  format: 'glb' | 'obj';
  creator: string;
  sourceUrl: string;
  license: 'CC0 1.0';
  use: string;
  status: 'production' | 'candidate';
  targetHeight: number;
}

const NATURE = '/assets/environment/kenney-nature';
const CASTLE = '/assets/environment/kenney-castle';

/**
 * Auditable model manifest. New online candidates enter the game here before a production rig
 * is allowed to depend on them, keeping attribution, scale, and fallback decisions visible.
 */
export const IMPORTED_MODEL_ASSETS: readonly ImportedModelAsset[] = [
  {
    id: 'kenney-rock-tall-a', name: 'Tall Granite A', category: 'terrain',
    path: `${NATURE}/rock_tallA.glb`, format: 'glb', creator: 'Kenney',
    sourceUrl: 'https://kenney.nl/assets/nature-kit', license: 'CC0 1.0',
    use: 'Mountain walls and map silhouettes', status: 'production', targetHeight: 5,
  },
  {
    id: 'kenney-rock-tall-b', name: 'Tall Granite B', category: 'terrain',
    path: `${NATURE}/rock_tallB.glb`, format: 'glb', creator: 'Kenney',
    sourceUrl: 'https://kenney.nl/assets/nature-kit', license: 'CC0 1.0',
    use: 'Mountain walls and map silhouettes', status: 'production', targetHeight: 5,
  },
  {
    id: 'kenney-rock-large-d', name: 'Large Boulder D', category: 'terrain',
    path: `${NATURE}/rock_largeD.glb`, format: 'glb', creator: 'Kenney',
    sourceUrl: 'https://kenney.nl/assets/nature-kit', license: 'CC0 1.0',
    use: 'Ridge connectors and outcrops', status: 'production', targetHeight: 2.4,
  },
  {
    id: 'kenney-castle-rubble-large', name: 'Castle Rubble (Large)', category: 'fortification',
    path: `${CASTLE}/rocks-large.glb`, format: 'glb', creator: 'Kenney',
    sourceUrl: 'https://kenney.nl/assets/castle-kit', license: 'CC0 1.0',
    use: 'Wall foundations and destroyed fortifications', status: 'production', targetHeight: 1.2,
  },
  {
    id: 'kenney-castle-wall-stud', name: 'Castle Wall Stud', category: 'fortification',
    path: `${CASTLE}/wall-stud.glb`, format: 'glb', creator: 'Kenney',
    sourceUrl: 'https://kenney.nl/assets/castle-kit', license: 'CC0 1.0',
    use: 'Wall-foundation structure', status: 'production', targetHeight: 1.8,
  },
  {
    id: 'aredon-crystal-cluster', name: 'Low-poly Crystal Cluster', category: 'resource',
    path: '/assets/environment/aredon-crystals/crystal-cluster.obj', format: 'obj',
    creator: 'Aredon', sourceUrl: 'https://opengameart.org/content/lowpoly-crystals',
    license: 'CC0 1.0', use: 'Power and Essence deposits', status: 'production', targetHeight: 2.2,
  },
] as const;

export interface OnlineModelCandidate {
  name: string;
  creator: string;
  sourceUrl: string;
  license: string;
  bestFor: string;
  assessment: string;
}

/** Reviewed replacements that are not shipped until silhouette and animation UAT approves them. */
export const ONLINE_MODEL_CANDIDATES: readonly OnlineModelCandidate[] = [
  {
    name: 'KayKit Adventurers', creator: 'Kay Lousberg',
    sourceUrl: 'https://kaylousberg.itch.io/kaykit-adventurers', license: 'CC0',
    bestFor: 'Iron Covenant and human unit animation baselines',
    assessment: 'Strong animation-ready baseline; requires faction armor, weapons, and color-language pass.',
  },
  {
    name: 'KayKit Skeletons', creator: 'Kay Lousberg',
    sourceUrl: 'https://kaylousberg.itch.io/kaykit-skeletons', license: 'CC0',
    bestFor: 'Ashen Host infantry baseline',
    assessment: 'Good readability and rig consistency; bespoke corpse-engine silhouettes should remain custom.',
  },
  {
    name: 'Quaternius Ultimate Animated Animals', creator: 'Quaternius',
    sourceUrl: 'https://quaternius.com/packs/ultimateanimatedanimals.html', license: 'CC0',
    bestFor: 'Verdant beasts and mounts',
    assessment: 'Useful motion and anatomy source; needs bark, thorn, and bloom overlays to match the faction.',
  },
];

const gltfLoader = new GLTFLoader(createModelLoadingManager());
const objLoader = new OBJLoader();
const sourceCache = new Map<string, Promise<THREE.Object3D>>();

function sourceFor(asset: ImportedModelAsset): Promise<THREE.Object3D> {
  let pending = sourceCache.get(asset.path);
  if (!pending) {
    pending = asset.format === 'glb'
      ? gltfLoader.loadAsync(asset.path).then((result) => result.scene)
      : objLoader.loadAsync(asset.path);
    sourceCache.set(asset.path, pending);
  }
  return pending;
}

/** Loads an isolated, grounded copy so Art Lab disposal never invalidates the production cache. */
export async function loadImportedModel(asset: ImportedModelAsset): Promise<THREE.Group> {
  const copy = cloneSkeleton(await sourceFor(asset));
  copy.traverse((child) => {
    if (!(child instanceof THREE.Mesh)) return;
    child.geometry = child.geometry.clone();
    child.material = Array.isArray(child.material)
      ? child.material.map((material) => material.clone())
      : child.material.clone();
    child.castShadow = true;
    child.receiveShadow = true;
  });

  copy.updateMatrixWorld(true);
  const initial = new THREE.Box3().setFromObject(copy);
  const size = initial.getSize(new THREE.Vector3());
  copy.scale.setScalar(asset.targetHeight / Math.max(0.001, size.y));
  copy.updateMatrixWorld(true);
  const fitted = new THREE.Box3().setFromObject(copy);
  const center = fitted.getCenter(new THREE.Vector3());
  copy.position.set(-center.x, -fitted.min.y, -center.z);

  const group = new THREE.Group();
  group.name = asset.id;
  group.add(copy);
  return group;
}
